import type { MomText } from '../../phone/momTexts';

/**
 * Phase D texts (family): Mom's morning texts, the balcony garden and the chiffon cake.
 * Lines marked "EN practice" are English the player reads aloud or picks as a reply.
 */
export const enFamily = {
  family: {
    mapNames: { balcony: 'Balcony' } as Record<string, string>,
    zones: { balcony: 'Balcony garden' },
    objects: {
      fam_plot: 'Planter',
      fam_tap: 'Water tap',
    },
    npc: {} as Record<string, string>,
    items: {
      tomato: { name: 'Tomato', desc: 'Juicy and sun-warm, from your balcony. Eat it for +20 energy.' },
      sunflower: { name: 'Sunflower', desc: 'A big smiling flower you grew yourself. Looking at it fills your mood to 100.' },
      chiffon: { name: 'Family-size chiffon cake', desc: 'A tall, fluffy 戚風蛋糕 made with three home-grown strawberries. Share it at home, or treat yourself.' },
    },

    // --- Mom's morning texts -----------------------------------------------------------
    menuMom: '📩 Mom’s messages',
    toastNew: '📩 Mom sent you a message!',
    previewNone: 'No messages yet',
    you: 'You',
    today: 'Today',
    sad: '😢',
    loveUp: (n: number) => `Mom ❤️ +${n}`,
    loveDown: 'Mom looks a little sad…',
    ps: {
      water: 'P.S. Don’t forget to water your plants on the balcony 🌱',
      ripe: 'P.S. I think something on the balcony is ready to pick! 🍅🍓',
    },
    /**
     * Mom's texts. `msgs` are hers ("@card" = a good-morning picture); `replies` are the
     * player's choices (EN practice), each with Mom's answers and an optional next turn.
     */
    texts: {
      rain: {
        icon: '☔',
        msgs: ['Good morning, sweetie ☔', 'It’s going to rain today. Take an umbrella! The blue one is by the shoe cabinet.'],
        replies: [
          {
            me: 'Got it, Mom! I’ll take the umbrella ☂️', tone: 'warm', love: 5,
            mom: ['Good kid 👍', 'And no jumping in puddles in your school shoes. I know you 😏'],
            next: [
              { me: 'Just ONE little puddle? 🥺', tone: 'warm', love: 3, mom: ['Hahaha. Okay, one. A very small one.', 'Then dry your shoes by the door when you get home!'] },
              { me: 'I promise. No puddles!', tone: 'ok', mom: ['I’ll believe it when I see dry socks 🧦😆'] },
            ],
          },
          {
            me: 'It’s only a little rain. I’ll be fine.', tone: 'ok',
            mom: ['A little rain in Taiwan turns into a big rain in five minutes! 🌧️', 'Please take it. For me?'],
            next: [
              { me: 'Okay, okay, I’ll take it. For you ❤️', tone: 'warm', love: 4, mom: ['Thank you, darling. Now I can work without worrying 😊'] },
              { me: 'Mom, you worry too much. Stop it.', tone: 'rude', mom: ['…Okay.', 'Moms are made to worry. Call me if you get wet.'] },
            ],
          },
          {
            me: 'Ugh, umbrellas are so annoying.', tone: 'rude',
            mom: ['Getting sick is more annoying…', 'Your little sister takes her raincoat. Even she listens to me!'],
            next: [
              { me: 'Sorry, Mom. I’ll take it.', tone: 'warm', love: 3, mom: ['Thank you 🥰 That’s my kid.'] },
              { me: 'Fine… but it’s ugly.', tone: 'ok', mom: ['Then buy a cute one with your pocket money! 😂'] },
            ],
          },
        ],
      },
      storm: {
        icon: '⛈️',
        msgs: ['⛈️ Thunderstorms this afternoon!', 'Umbrella in your bag. If there’s lightning, wait inside. Don’t stand under trees!'],
        replies: [
          {
            me: 'I’ll wait inside until it stops. Promise! ⛈️', tone: 'warm', love: 6,
            mom: ['That’s my smart kid 🧠✨', 'If it’s really bad, call me and I’ll pick you up.'],
            next: [
              { me: 'Thanks, Mom. Can we have hot soup tonight? 🍲', tone: 'warm', love: 3, mom: ['Corn soup, your favorite. Deal 😋'] },
              { me: 'Can Dad come instead? His car has music 🎶', tone: 'ok', mom: ['Hmph. My scooter has music too. I sing! 🛵🎤😂'] },
            ],
          },
          {
            me: 'Thunder is cool! Can I watch the lightning?', tone: 'ok',
            mom: ['From the WINDOW, yes. From the field, NO ⚡', 'Lightning is not a video, sweetie 😅'],
            next: [
              { me: 'Haha, okay. From the window. Love you!', tone: 'warm', love: 4, mom: ['Love you more ❤️ Stay dry!'] },
              { me: 'You never let me do anything fun.', tone: 'rude', mom: ['I let you do lots of fun things… just not dangerous ones.', 'Let’s watch the storm together tonight, with snacks. Okay?'] },
            ],
          },
          {
            me: 'I’m not a baby. Stop texting me.', tone: 'rude',
            mom: ['Okay. I just love you, that’s all.'],
            next: [{ me: 'Sorry, Mom… I love you too.', tone: 'warm', love: 4, mom: ['🥹 Okay. Now go, or you’ll be late!'] }],
          },
        ],
      },
      sunny: {
        icon: '☀️',
        msgs: ['Good morning! ☀️ Sunny and warm today.', 'Drink lots of water! Your bottle is full — don’t bring it home full again 😤'],
        replies: [
          {
            me: 'I’ll drink it ALL. Every drop! 💧', tone: 'warm', love: 5,
            mom: ['Hahaha. I’ll check the bottle tonight 🕵️‍♀️'],
            next: [
              { me: 'Deal! And I’ll fill it again at school.', tone: 'warm', love: 3, mom: ['Wow. Who are you, and where is my child? 😂❤️'] },
              { me: 'What do I get if it’s empty?', tone: 'ok', mom: ['A hug. And maybe a bubble tea on Sunday 🧋', 'Half sugar!'] },
            ],
          },
          {
            me: 'But the school water tastes funny.', tone: 'ok',
            mom: ['It’s just water! Okay… put a slice of lemon in it.', 'There are lemons in the fridge 🍋'],
            next: [
              { me: 'Lemon water! Good idea, thanks Mom 🍋', tone: 'warm', love: 4, mom: ['I’m full of good ideas. Tell your dad 😎'] },
              { me: 'Lemon water is gross.', tone: 'rude', mom: ['Grandma has drunk lemon water for 80 years…', 'Okay, plain water then. Just DRINK it!'] },
            ],
          },
          {
            me: 'Water is boring. I want soda.', tone: 'rude',
            mom: ['Soda has SO much sugar…', 'Your teeth will cry. And then I will cry.'],
            next: [{ me: 'Okay, water. For my teeth and for you 😁', tone: 'warm', love: 3, mom: ['Good! Show me your shiny teeth tonight 😁🦷'] }],
          },
        ],
      },
      hot: {
        icon: '🥵',
        msgs: ['🥵 It’s going to be 33 degrees today!', 'Wear your hat, drink water every break, and stay in the shade at lunchtime.'],
        replies: [
          {
            me: 'Hat on! I look super cool 🧢', tone: 'warm', love: 5,
            mom: ['The coolest kid in Taiwan 😎', 'Sunscreen too! It’s on the shelf by the door.'],
            next: [
              { me: 'Got it! Sunscreen, hat, water ✅', tone: 'warm', love: 3, mom: ['Perfect checklist! Teacher Mom gives you 100 points 💯'] },
              { me: 'Sunscreen is sticky…', tone: 'ok', mom: ['Sticky is better than red like a shrimp 🦐😂'] },
            ],
          },
          {
            me: 'Can I buy a shaved ice after school? 🍧', tone: 'ok',
            mom: ['Hmm… one small mango shaved ice.', 'If your water bottle is empty first!'],
            next: [
              { me: 'Yes! Thank you, best Mom ever! 🥭', tone: 'warm', love: 4, mom: ['Best Mom ever wants a bite too 😋'] },
              { me: 'Can I get a BIG one?', tone: 'ok', mom: ['Small. Your tummy is small 😆'] },
            ],
          },
          {
            me: 'I hate hats. I’m not wearing it.', tone: 'rude',
            mom: ['Then the sun will give you a headache…', 'At least stay in the shade, please?'],
            next: [{ me: 'Sorry. I’ll wear it, Mom.', tone: 'warm', love: 3, mom: ['Thank you, sweetie. You’ll thank me in August 😂'] }],
          },
        ],
      },
      cool: {
        icon: '🍂',
        msgs: ['Good morning 🍂 It’s turning cool today, only 18 degrees.', 'Wear your jacket! Not carry it — WEAR it 🧥'],
        replies: [
          {
            me: 'Jacket is ON. Zipped up! 🧥', tone: 'warm', love: 5,
            mom: ['So cute and so warm 😍', 'There’s a warm drink in your thermos too.'],
            next: [
              { me: 'Thanks, Mom! Is it ginger tea? 🫚', tone: 'warm', love: 3, mom: ['Ginger tea with brown sugar. Grandma’s recipe ❤️'] },
              { me: 'Do I have to drink all of it?', tone: 'ok', mom: ['Yes. It’s magic. It keeps colds away 🪄😆'] },
            ],
          },
          {
            me: '18 degrees isn’t cold!', tone: 'ok',
            mom: ['For Taiwan it’s FREEZING 🥶😂', 'Everyone at school will be in a down jacket. Trust me.'],
            next: [
              { me: 'Haha, okay. I’ll wear it so you don’t worry.', tone: 'warm', love: 4, mom: ['Thank you, darling. A happy mom is a quiet mom 😌'] },
              { me: 'Down jackets are for old people.', tone: 'rude', mom: ['I wear a down jacket…', 'Okay, okay. At least bring it, please?'] },
            ],
          },
          {
            me: 'I don’t need a jacket. Leave me alone.', tone: 'rude',
            mom: ['When you catch a cold, who makes the soup? Me.'],
            next: [{ me: 'Sorry, Mom… I’ll wear it.', tone: 'warm', love: 3, mom: ['Good. And I’ll make soup anyway, just in case 🍲'] }],
          },
        ],
      },
      typhoon: {
        icon: '🌀',
        msgs: ['🌀 Typhoon day! No school, no work — everybody stays home.', 'Can you help me get ready? Bring the balcony plants in, fill bottles with water and charge the flashlight 🔦'],
        replies: [
          {
            me: 'Of course! I’ll help with everything 💪', tone: 'warm', love: 7,
            mom: ['My little hero 🦸', 'Plants first, before the wind gets strong!'],
            next: [
              { me: 'On it! Plants first, then the water bottles.', tone: 'warm', love: 3, mom: ['What a team. Then instant noodles and card games 🍜🃏'] },
              { me: 'Can we watch the typhoon from the window?', tone: 'ok', mom: ['From far away from the glass, yes. Windows can break!', 'Tape on the windows first 🙂'] },
            ],
          },
          {
            me: 'A typhoon day! Can I play games all day? 🎮', tone: 'ok',
            mom: ['Hahaha. Every kid in Taiwan says that 😂', 'Help first, then games. Deal?'],
            next: [
              { me: 'Deal! Helping first 💪', tone: 'warm', love: 4, mom: ['That’s my kid! I’ll make your favorite noodles 🍜'] },
              { me: 'No. It’s a holiday!', tone: 'rude', mom: ['It’s not a holiday for the people who fix the power lines…', 'Let’s be thankful and help a little, okay?'] },
            ],
          },
          {
            me: 'That’s boring. Ask my sister.', tone: 'rude',
            mom: ['Your sister is six. She can’t carry the plant pots.', 'I really need you today.'],
            next: [{ me: 'Sorry. I’ll help you, Mom.', tone: 'warm', love: 4, mom: ['Thank you, sweetie. We’ll be safe together ❤️'] }],
          },
        ],
      },
      exam: {
        icon: '📝',
        msgs: ['Good luck on your test today! 📝', 'I put a zongzi in your lunch box. “Bāo zhòng” — it means you’ll pass! 😆'],
        replies: [
          {
            me: 'Thanks, Mom! I studied a lot. I’ll do my best! 📚', tone: 'warm', love: 6,
            mom: ['I know. I saw your light on last night 💡', 'Whatever the score, I’m proud of you.'],
            next: [
              { me: 'That means a lot. Love you ❤️', tone: 'warm', love: 3, mom: ['❤️❤️❤️ Now go show that test who’s boss!'] },
              { me: 'Even if I get a 60?', tone: 'ok', mom: ['Even 60. But 61 is better 😂', 'Read every question twice!'] },
            ],
          },
          {
            me: 'I’m so nervous…', tone: 'ok',
            mom: ['Take three slow breaths. In… and out… 🌬️', 'Nervous means you care. That’s a good thing.'],
            next: [
              { me: 'Okay… breathing. Thanks, Mom. I feel better.', tone: 'warm', love: 5, mom: ['You’ve got this! I’m cheering for you from work 📣'] },
              { me: 'What if I forget everything?', tone: 'ok', mom: ['You won’t. And if you do, it’s one test, not your whole life 🤗'] },
            ],
          },
          {
            me: 'Don’t remind me! You’re making it worse!', tone: 'rude',
            mom: ['Sorry… I just wanted to cheer you on.'],
            next: [{ me: 'Sorry, Mom. I’m just stressed. Thank you.', tone: 'warm', love: 4, mom: ['I know, sweetie. Big hug 🤗 Zongzi power!'] }],
          },
        ],
      },
      dinner: {
        icon: '🍗',
        msgs: ['Good morning! Guess what’s for dinner tonight? 😋', 'Three-cup chicken! 三杯雞 — with lots of basil 🌿'],
        replies: [
          {
            me: 'YES! My favorite! Can I help you cook? 🍗', tone: 'warm', love: 6,
            mom: ['Of course! You can pick the basil and stir the pot.', 'Be home by six, chef! 👩‍🍳'],
            next: [
              { me: 'Chef reporting for duty at six! 🫡', tone: 'warm', love: 3, mom: ['Hahaha, chef! I’ll find you an apron 😆'] },
              { me: 'Can I just do the tasting part? 😋', tone: 'ok', mom: ['The taster washes the dishes 😏'] },
            ],
          },
          {
            me: 'What are the three cups?', tone: 'ok',
            mom: ['Sesame oil, rice wine and soy sauce — one cup of each! 🍶', 'Now you know a family secret 🤫'],
            next: [
              { me: 'Cool! Will you teach me the recipe someday?', tone: 'warm', love: 4, mom: ['Someday? This weekend! 🥰'] },
              { me: 'Is there a cup of chocolate? 🍫', tone: 'ok', mom: ['Hahaha, no! That’s dessert, silly 😂'] },
            ],
          },
          {
            me: 'Chicken again? Boring.', tone: 'rude',
            mom: ['I get up early to buy the chicken at the market…'],
            next: [
              { me: 'Sorry, Mom. I love your three-cup chicken. Really!', tone: 'warm', love: 4, mom: ['Hmph… okay. You get the biggest piece 😌'] },
              { me: 'Can we have pizza instead?', tone: 'ok', mom: ['Pizza on Saturday. Three-cup chicken today. Mom’s rules 📏'] },
            ],
          },
        ],
      },
      card: {
        icon: '🌻',
        card: { theme: 1, big: 'Good morning', sub: 'Peace and health every day' },
        msgs: ['@card', 'Good morning, my dear 🌸🌸 Peace and health every day! 🙏'],
        replies: [
          {
            me: 'Good morning, Mom! Pretty flowers 🌻', tone: 'warm', love: 5,
            mom: ['Aren’t they? Auntie Lin sent it to me first 😆', 'I forwarded it to forty people.'],
            next: [
              { me: 'Forty?! You’re the queen of good-morning pictures 👑', tone: 'warm', love: 3, mom: ['Thank you, thank you 👸 I have 300 more saved 😂'] },
              { me: 'Mom… you don’t have to send one every day 😅', tone: 'ok', mom: ['But then how will you know I’m okay? 😂', 'One picture a day = Mom is fine!'] },
            ],
          },
          {
            me: '@card', tone: 'warm', love: 5,
            mom: ['WOW! My kid sent ME a picture! 🥹', 'I’m showing everyone at work.'],
            next: [
              { me: 'Please don’t show everyone 😳', tone: 'ok', mom: ['Too late. Mrs. Chen says you’re adorable 😆'] },
              { me: 'Tell them I said hi! 👋', tone: 'warm', love: 2, mom: ['They all said hi back! You’re famous 🌟'] },
            ],
          },
          {
            me: 'Not another one of those pictures 🙄', tone: 'rude',
            mom: ['It took me three minutes to choose that one…'],
            next: [{ me: 'Sorry, Mom. It IS pretty 🌸', tone: 'warm', love: 3, mom: ['Hehe. Tomorrow I’ll choose one with a cat 🐱'] }],
          },
        ],
      },
    } satisfies Record<string, MomText>,
    /** Label of the "send a picture back" reply. */
    sendCard: 'Send a good-morning picture back 🖼️',
    replyCard: { theme: 4, big: 'Good morning', sub: 'Love you, Mom' },

    // --- Balcony garden ----------------------------------------------------------------
    crops: {
      strawberry: { name: 'Strawberry', icon: '🍓', seed: 'Strawberry seeds' },
      tomato: { name: 'Tomato', icon: '🍅', seed: 'Tomato seeds' },
      sunflower: { name: 'Sunflower', icon: '🌻', seed: 'Sunflower seeds' },
    },
    stages: ['Empty soil', 'Seedling', 'Sprout', 'Growing', 'Ripe!'],
    plotTitle: (n: number) => `Planter ${n}`,
    emptyText: 'Soft, dark soil. What do you want to plant?', // EN practice
    plant: (seed: string) => `🌱 Plant ${seed}`,
    free: 'free',
    notNow: 'Not now',
    noCoins: 'Not enough coins for those seeds.',
    planted: (crop: string) => `You planted ${crop}! Water it once a day.`, // EN practice
    watered: (n: number) => `💧 Watered! (${n}/3)`,
    rainWatered: '🌧️ The rain watered your plants today.',
    alreadyWatered: 'The soil is still damp. Come back tomorrow!',
    canEmpty: 'The watering can is empty — fill it at the tap.',
    harvest: (what: string) => `Harvest time! You picked ${what}.`, // EN practice
    growText: (crop: string, stage: string, n: number) => `${crop} · ${stage} · watered ${n}/3 days`,
    covered: 'The plants are safe under the tarp until the typhoon passes.',
    tapTitle: 'Water tap',
    tapText: 'A rusty old tap with a blue bucket. The watering can is next to it.',
    fillCan: (n: number) => `🪣 Fill the watering can (${n} waterings)`,
    canFilled: 'Glug, glug… the watering can is full!',
    fillBottle: '💧 Fill your water bottle',
    bottleFilled: 'Your water bottle is full again.',
    canState: (n: number) => `Watering can: ${n} left`,
    plantsSafe: '🪴 The balcony plants are safe inside. Good thinking!',
    typhoonWarn: '🌀 Typhoon today! Bring the balcony plants in before tonight.',
    typhoonHit: '🌀 Last night’s typhoon flattened the balcony plants… they have to grow again.',
    tomatoEat: 'Sweet and juicy! +20 energy.',
    sunflowerLook: 'The sunflower smiles at you. You feel wonderful!',

    // --- Oven: chiffon cake ------------------------------------------------------------
    chiffon: '🎂 Bake a family-size chiffon cake 戚風蛋糕',
    chiffonNeeds: 'A chiffon cake needs 3 strawberries. Grow them on the balcony!',
    chiffonWork: 'Separating eggs, whipping the meringue, folding in strawberries…',
    chiffonDone: 'Tall and fluffy as a cloud! A family-size 戚風蛋糕!',
    chiffonShare: 'Everyone gathers round. “So soft! Like a cloud!” The whole family is smiling.',
    chiffonEat: 'You eat a big fluffy slice. Pure happiness!',

    journal: {
      harvest: 'Harvested {arg} from the balcony garden',
      chiffon: 'Baked a family-size chiffon cake with home-grown strawberries',
    },
  },
};
